import dayjs from "dayjs";
import _ from "lodash";

import { User } from "@/api/generated/api";
import { PropertyHolder } from "@/view_models/atoms";

// ------------------------------------------------------------
// 軽量ハッシュ関数（文字列 → 32bit整数 → 36進数文字列）
// キャッシュキーに使いやすいように短い文字列に変換する
// ------------------------------------------------------------
const hashString = (input: string): string => {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0; // 32-bit に丸める
  }
  return Math.abs(h).toString(36); // 符号を外して36進数化
};

// ------------------------------------------------------------
// プロパティのフィンガープリント作成
// → 月次キャッシュ結果に影響する最小限の情報だけを抽出
//    これをハッシュ化してキャッシュキーに利用する
// ------------------------------------------------------------
export const buildPropertyFingerprintPayload = (properties: PropertyHolder[]) => {
  return properties.map((p) => ({
    id: p.id,
    // 物件本体の基本情報（購入日・築年・ローン・取得税など）
    property: _.pick(p.property, [
      "property_type",
      "property_category",
      "updated_at",
      "bought_at",
      "built_at",
      "total_price_of_property",
      "loan_amount",
      "payment_period",
      "payment_starts_at",
      "property_acquisition_tax",
      "property_acquisition_tax_payment_month",
      "misc_expences_at_bought",
      "structure",
      "priceof_tax",
      "priceof_building",
      "priceof_land",
      "priceof_facilities",
    ]),
    // 一棟物件の基本月額経費
    building_detail: _.pick(p.property.building_detail, [
      "management_company_commission",
      "shared_utility_expenses",
      "misc_building_cost",
      "updated_at",
    ]),
    // 賃貸契約に関わる情報
    lease_contracts:
      p.lease_contracts?.map((c) =>
        _.pick(c, [
          "starts_at",
          "ends_at",
          "monthly_fee",
          "monthly_management_fee",
          "other_monthly_income",
          "key_money",
          "security_deposit",
          "outsourcing_fee_rate_of_new_lease_contract",
          "outsourcing_fee_rate_of_updating_lease_contract",
          "updated_at",
        ])
      ) ?? [],
    // 部屋ごとの賃料・収入
    building_room:
      p.building_room?.map((r) =>
        _.pick(r, ["starts_at", "ends_at", "monthly_fee", "other_monthly_income", "updated_at"])
      ) ?? [],
    // シミュレーション設定（家賃・市場価格・積立変更など）
    simulation_configs:
      p.simulation_configs?.map((s) => ({
        proposed_by: s.proposed_by,
        house_rent_mode: s.house_rent_mode,
        house_rent_price_change_ends_at: s.house_rent_price_change_ends_at,
        house_rent_price_change_down_value: s.house_rent_price_change_down_value,
        house_rent_spare_months: s.house_rent_spare_months,
        house_rent_spare_months_for_N_contracts: s.house_rent_spare_months_for_N_contracts,
        market_price_mode: s.market_price_mode,
        market_price_change_ends_at: s.market_price_change_ends_at,
        market_price_change_down_value: s.market_price_change_down_value,
        market_price_change_at_bought_mode: s.market_price_change_at_bought_mode,
        market_price_change_at_bought_value: s.market_price_change_at_bought_value,
        reserve_fund_price_changes: s.reserve_fund_price_changes?.map((rf) => _.pick(rf, ["month", "amount"])) ?? [],
      })) ?? [],
    // ユーザーが入力した月次キャッシュフロー修正（任意の上書き）
    user_input_monthly_cashflow_results:
      p.user_input_monthly_cashflow_results?.map((u) =>
        _.pick(u, [
          "month",
          "management_cost",
          "building_management_cost",
          "repair_fund_cost",
          "communication_cost",
          "misc_cost",
          "loan_interest",
          "loan_interest_rate_percentage",
          "loan_principal",
          "loan_prepayment",
          "loan_prepayment_method",
          "key_money",
          "security_deposit",
          "property_tax",
          "fire_insurance_cost",
          "fire_insurance_holding_period",
          "earthquake_insurance_cost",
          "earthquake_insurance_holding_period",
          "large_scale_repair_fund_cost",
          "supplies_cost",
          "misc_onetime_cost",
          "tax_on_real_estates",
          "outsourcing_fee_of_new_lease_contract",
          "outsourcing_fee_of_updating_lease_contract",
          "updated_at",
        ])
      ) ?? [],
  }));
};

const buildUserFingerprintPayload = (user?: User | null) => ({
  income_tax_rate: user?.income_tax_rate,
});

// ------------------------------------------------------------
// 今月のキャッシュ結果用キー作成
// フォーマット: propally:cf:this-month:{version}:{YYYYMM}:{fingerprint}
// ------------------------------------------------------------
export const buildThisMonthCacheKey = (properties: PropertyHolder[], user?: User | null): string => {
  const version = "v2"; // フィンガープリント構造を変更したら上げる
  const month = dayjs().format("YYYYMM"); // 今月（キーの月次スコープ）
  const payload = {
    properties: buildPropertyFingerprintPayload(properties), // 最小情報を抽出
    user: buildUserFingerprintPayload(user),
  };
  const fingerprint = hashString(JSON.stringify(payload)); // JSON化してハッシュ
  return `propally:cf:this-month:${version}:${month}:${fingerprint}`;
};

// ------------------------------------------------------------
// 今月キャッシュの読込
// ------------------------------------------------------------
export const readThisMonthCache = <T>(properties: PropertyHolder[], user?: User | null): T | null => {
  try {
    const key = buildThisMonthCacheKey(properties, user);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch (e) {
    return null; // localStorageやJSON parse失敗時はnull
  }
};

// ------------------------------------------------------------
// 今月キャッシュの書込
// ------------------------------------------------------------
export const writeThisMonthCache = (properties: PropertyHolder[], value: unknown, user?: User | null): void => {
  try {
    const key = buildThisMonthCacheKey(properties, user);
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // localStorage容量オーバーなどは握りつぶす
  }
};

// ------------------------------------------------------------
// 今月のローン情報キャッシュ用キー作成
// フォーマット: propally:loan-info:this-month:{version}:{YYYYMM}:{fingerprint}
// ------------------------------------------------------------
const buildLoanInfoCacheKey = (properties: PropertyHolder[]): string => {
  const version = "v1";
  const month = dayjs().format("YYYYMM");
  const payload = buildPropertyFingerprintPayload(properties);
  const fingerprint = hashString(JSON.stringify(payload));
  return `propally:loan-info:this-month:${version}:${month}:${fingerprint}`;
};

// ------------------------------------------------------------
// 今月のローン情報キャッシュ読込
// ------------------------------------------------------------
export const readThisMonthLoanInfoCache = <T>(properties: PropertyHolder[]): T | null => {
  try {
    const key = buildLoanInfoCacheKey(properties);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch (e) {
    return null;
  }
};

// ------------------------------------------------------------
// 今月のローン情報キャッシュ書込
// ------------------------------------------------------------
export const writeThisMonthLoanInfoCache = (properties: PropertyHolder[], value: unknown): void => {
  try {
    const key = buildLoanInfoCacheKey(properties);
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    // 特に何もしない
  }
};
